import React from 'react';

/**
 * App-wide error boundary.
 *
 * Without this, any uncaught render/lifecycle error unmounts the entire React
 * tree and the user sees a blank white screen. This catches that error, keeps
 * the rest of the app shell alive, and shows a friendly recovery panel instead.
 *
 * Intentionally built with plain inline styles (no MUI, no context, no router):
 * the boundary must be able to render even if the thing that crashed was the
 * theme/provider/router itself.
 */
const CHUNK_RELOAD_KEY = 'xeo:chunk-reload';

/*
 * 100dvh where the browser has it, the shell's rule (Loader, Dashboard): on a
 * phone 100vh is the height with the toolbars HIDDEN, so the panel centred half
 * a toolbar low and the page gained a scroll it had no use for. Asked in script
 * because an inline style can't carry the shell's @supports block.
 */
const FULL_HEIGHT = typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    && CSS.supports('height', '100dvh') ? '100dvh' : '100vh';

/*
 * How long the app must stay up before a reload is considered to have worked.
 * Long enough to outlast the navigation that would throw again on a genuinely
 * broken deploy, short enough that a user who sits on one page for a minute and
 * then hits a NEW deploy still gets their one reload.
 */
const CHUNK_GUARD_HOLD_MS = 30000;

/**
 * Did this fail because a code-split chunk could not be fetched?
 *
 * Matched on name AND message: webpack throws a ChunkLoadError by name, but a
 * bare network failure on the same request surfaces as a plain Error whose
 * message names the chunk. Missing either one leaves a user staring at an error
 * page after a routine deploy.
 */
export function isChunkLoadError(error) {
    const name = String(error?.name || '');
    const msg = String(error?.message || '');
    return name === 'ChunkLoadError'
        || /Loading chunk [\w-]+ failed/i.test(msg)
        || /Loading CSS chunk/i.test(msg)
        || /error loading dynamically imported module/i.test(msg);
}

/**
 * The few facts that identify a crash: what threw, where it was rendered, on
 * which page and build, and when. Shown under "Show details" and copied by
 * "Copy details" — no data from the page itself, only the error and the route.
 */
export function errorDetails(error, info) {
    const firstFrames = (txt, n) => String(txt || '').split('\n').map((l) => l.trim()).filter(Boolean).slice(0, n).join('\n');
    let build = '';
    try { build = ([...document.scripts].map((x) => x.src).find((src) => /\/main\.[a-f0-9]+\.js/.test(src)) || '').replace(/^.*\//, ''); } catch (_) { /* none */ }
    let page = '';
    try { page = `${window.location.pathname || ''}${window.location.search || ''}`; } catch (_) { /* none */ }
    return [
        `Error: ${error?.name || 'Error'}: ${error?.message || String(error)}`,
        `Page: ${page}`,
        `Build: ${build || 'dev'}`,
        `Time: ${new Date().toISOString()}`,
        info && info.componentStack ? `Component:\n${firstFrames(info.componentStack, 4)}` : '',
        error?.stack ? `Stack:\n${firstFrames(error.stack, 5)}` : '',
    ].filter(Boolean).join('\n');
}

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidMount() {
        /*
         * CLEAR THE GUARD ONLY ONCE THE APP HAS ACTUALLY SURVIVED.
         *
         * This cleared on mount, which defeated the thing it was guarding. The
         * reload lands, the boundary mounts, the key is removed — and THEN the
         * user navigates and the missing chunk throws again. With the key gone
         * that second failure looks like the first, so it reloads again, and a
         * permanently broken deploy refreshes for ever and never shows the
         * message explaining why. Exactly the loop the key exists to stop.
         *
         * Mounting is not evidence the app works: the boundary mounts at the
         * root, and a ChunkLoadError comes from a LATER route load. Surviving a
         * while is evidence. If it throws inside the window the guard is still
         * set and the message is shown; if it does not, a future deploy gets its
         * own reload as intended.
         */
        this.chunkGuardTimer = setTimeout(() => {
            try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (_) { /* fine */ }
        }, CHUNK_GUARD_HOLD_MS);
    }

    componentWillUnmount() {
        if (this.chunkGuardTimer) clearTimeout(this.chunkGuardTimer);
    }

    componentDidCatch(error, info) {
        /*
         * A CHUNK THAT WILL NEVER ARRIVE IS NOT AN ERROR THE USER CAN ACT ON.
         *
         * Route-level code splitting created a failure mode that did not exist
         * when everything shipped in one file: a tab left open across a deploy
         * holds an index.html pointing at chunk hashes the server no longer has.
         * The next navigation throws ChunkLoadError, and the user gets an error
         * screen for an app that is working perfectly — they just have yesterday's
         * map of it.
         *
         * Reloading genuinely fixes that, so it reloads, ONCE. The sessionStorage
         * key is what stops a reload loop if the chunk is missing for some other
         * reason: without it a permanently broken deploy would refresh the page
         * forever and never show the message explaining why.
         */
        /*
         * THE HOLD TIMER IS MEASURING "THE APP STAYED UP". It did not — we are
         * in the fallback, whatever threw. Letting it run clears the guard 30s
         * into an error screen, so a user who reads the message and then presses
         * Reload gets one automatic reload they did not ask for on top of their
         * own.
         *
         * Cancelled for EVERY caught error, not only chunk ones. The rationale
         * above never mentioned chunks — it is about being in the fallback — and
         * the guard it protects is the chunk one, so a non-chunk crash followed
         * by a chunk error is exactly the sequence where the stale timer does
         * the damage.
         */
        if (this.chunkGuardTimer) { clearTimeout(this.chunkGuardTimer); this.chunkGuardTimer = null; }
        if (isChunkLoadError(error)) {
            try {
                if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
                    window.location.reload();
                    return;
                }
            } catch (_) { /* no sessionStorage — fall through to the message */ }
        }
        // Surface in the console for diagnostics; swap for a logging service later.
        console.error('[ErrorBoundary] Uncaught error:', error, info && info.componentStack);
        // What the panel's "Show details" says, so a user can send us the exact cause.
        this.setState({ details: errorDetails(error, info) });
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div style={{
                minHeight: FULL_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24, background: 'var(--surface-2)',
                fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Inter,system-ui,sans-serif",
            }}>
                <div style={{
                    maxWidth: 440, width: '100%', textAlign: 'center', background: 'var(--surface)',
                    border: '1px solid var(--line)', borderRadius: 16, padding: '36px 32px',
                    boxShadow: '0 14px 40px rgba(15,31,61,0.10)',
                }}>
                    <div style={{
                        width: 56, height: 56, margin: '0 auto 18px', borderRadius: '50%',
                        background: 'var(--bad-bg)', color: 'var(--bad-fg)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 28,
                    }}>⚠️</div>
                    <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink)', margin: '0 0 8px' }}>
                        Something went wrong
                    </h1>
                    <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.55, margin: '0 0 24px' }}>
                        This page hit an unexpected error. Your data is safe — reloading usually fixes it.
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button onClick={() => window.location.reload()} style={{
                            border: 0, cursor: 'pointer', borderRadius: 10, padding: '10px 18px',
                            fontSize: 14, fontWeight: 700, color: '#fff',
                            background: 'linear-gradient(140deg,#2563eb,#1d4ed8)',
                        }}>Reload page</button>
                        <button onClick={() => window.location.assign('/dashboard')} style={{
                            border: '1px solid var(--line)', cursor: 'pointer', borderRadius: 10,
                            padding: '10px 18px', fontSize: 14, fontWeight: 700, color: 'var(--ink-2)', background: 'var(--surface)',
                        }}>Go to dashboard</button>
                    </div>
                    {this.state.details ? (
                        <details style={{ marginTop: 20, textAlign: 'left' }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'var(--ink-3)' }}>Show details</summary>
                            <pre data-error-details="" style={{
                                margin: '10px 0 8px', padding: 12, borderRadius: 10, background: 'var(--sunken)', color: 'var(--ink-2)',
                                fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflow: 'auto',
                            }}>{this.state.details}</pre>
                            <button onClick={() => { try { navigator.clipboard.writeText(this.state.details); this.setState({ copied: true }); } catch (_) { /* select it by hand */ } }} style={{
                                border: '1px solid var(--line)', cursor: 'pointer', borderRadius: 8, padding: '6px 12px',
                                fontSize: 13, fontWeight: 700, color: 'var(--ink-2)', background: 'var(--surface)',
                            }}>{this.state.copied ? 'Copied' : 'Copy details'}</button>
                            <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 10 }}>Send this to support so we can fix it.</span>
                        </details>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
